import React from 'react';
import { useUIState } from '@/core/UIStateProvider';
import { useCreateTrackingJob, useDeleteTrackingJob, useExecuteTrackingJob, useResumeTrackingJob } from '@/core/api/queries';

interface TrackingControlsProps {
  shipmentId: string;
  carrierName?: string;
  referenceNumber?: string;
  referenceType?: string;
  jobId?: string;
  isJobPaused?: boolean;
}

const TrackingControls: React.FC<TrackingControlsProps> = ({
  shipmentId,
  carrierName,
  referenceNumber,
  referenceType,
  jobId,
  isJobPaused = false
}) => {


  const { addNotification } = useUIState();
  const createTrackingJob = useCreateTrackingJob()
  const resumeTrackingJob = useResumeTrackingJob()
  const deleteTrackingJob = useDeleteTrackingJob()
  const executeTrackingJob = useExecuteTrackingJob()

  const handleStartTracking = async (): Promise<void> => {
    try {
      await createTrackingJob.mutateAsync({
        shipmentId,
        carrierName: carrierName!,
        referenceNumber: referenceNumber!,
        referenceType: referenceType!
      });
      addNotification('success', 'Tracking job started successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start tracking';
      addNotification('error', errorMessage);
    }
  };

  const handleResumeTracking = async (): Promise<void> => {
    if (!jobId) return;
    try {
      await resumeTrackingJob.mutateAsync(jobId);
      addNotification('success', 'Tracking job resumed');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resume tracking';
      addNotification('error', errorMessage);
    }
  };

  const handleExecuteTracking = async (): Promise<void> => {
    if (!jobId) return;
    try {
      await executeTrackingJob.mutateAsync(jobId);
      addNotification('success', 'Tracking job executed successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to execute tracking job';
      addNotification('error', errorMessage);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!jobId || !window.confirm('Are you sure you want to delete this tracking job?')) return;
    try {
      await deleteTrackingJob.mutateAsync(jobId);
      addNotification('success', 'Tracking job deleted');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete tracking job';
      addNotification('error', errorMessage);
    }
  };

  const isLoading = createTrackingJob.isPending || resumeTrackingJob.isPending || deleteTrackingJob.isPending || executeTrackingJob.isPending;

  return (
    <div className="tracking-controls">
      <div className="flex flex-wrap gap-3 my-4">
        <button
          onClick={handleStartTracking}
          disabled={isLoading || !!jobId || !carrierName || !referenceNumber || !referenceType}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
        >
          {createTrackingJob.isPending ? 'Starting...' : 'Start Tracking'}
        </button>

        {jobId && (
          <button
            onClick={handleExecuteTracking}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            {executeTrackingJob.isPending ? 'Executing...' : 'Execute Job'}
          </button>
        )}

        {jobId && isJobPaused && (
          <button
            onClick={handleResumeTracking}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            {resumeTrackingJob.isPending ? 'Resuming...' : 'Resume Tracking'}
          </button>
        )}

        {jobId && (
          <button
            onClick={handleDelete}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-900 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            {deleteTrackingJob.isPending ? 'Deleting...' : 'Delete Job'}
          </button>
        )}
      </div>
    </div>
  );
};

export default TrackingControls;